/* eslint-disable react-hooks/rules-of-hooks */
import * as React from 'react';
import { Text } from '@oneaudi/unified-web-components';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  cleanupEmissionConsumptionMarkup,
  getConsumptionsAndEmissions,
} from '@oneaudi/feature-app-utils';
import {
  renderTextWithFootnotesReferences,
  type LocaleService,
  type VueFormatterServiceInterface,
} from '../feature-service-types';

export interface ConsumptionAndEmissionsProps {
  caeIds?: string[];
  onCaeReady?: () => void;
  vueFormatterService: VueFormatterServiceInterface;
  localeService: LocaleService;
}

interface CaeData {
  consumption: string | ReactNode[];
  emissions: string | ReactNode[];
  emissionClass: string | ReactNode[];
  dischargedCO2Class?: string | ReactNode[];
  dischargedConsumption?: string | ReactNode[];
}

export const ConsumptionAndEmissions: FC<ConsumptionAndEmissionsProps> = ({
  caeIds,
  onCaeReady,
  vueFormatterService,
  localeService,
}) => {
  if (!caeIds || !caeIds.length) return null;

  const [caeData, setCaeData] = useState<CaeData[]>();

  useEffect(() => {
    (async () => {
      const consumptionsAndEmissionsValues = await getConsumptionsAndEmissions(
        caeIds,
        vueFormatterService,
        localeService,
      );

      const _caeData: CaeData[] = consumptionsAndEmissionsValues
        .map((cae): CaeData | null => {
          // Check if any of the required fields are defined
          if (cae && (cae.formattedConsumption || cae.formattedEmission || cae.formattedCo2Class)) {
            return {
              consumption: cae.formattedConsumption
                ? renderTextWithFootnotesReferences(
                    cleanupEmissionConsumptionMarkup(cae.formattedConsumption || ''),
                  )
                : '',
              emissions: cae.formattedEmission
                ? renderTextWithFootnotesReferences(
                    cleanupEmissionConsumptionMarkup(cae.formattedEmission || ''),
                  )
                : '',
              emissionClass: cae.formattedCo2Class
                ? renderTextWithFootnotesReferences(
                    cleanupEmissionConsumptionMarkup(cae.formattedCo2Class || ''),
                  )
                : '',
              dischargedConsumption: cae.formattedDischargedConsumption
                ? renderTextWithFootnotesReferences(
                    cleanupEmissionConsumptionMarkup(cae.formattedDischargedConsumption),
                  )
                : '',
              dischargedCO2Class: cae.formattedDischargedCo2Class
                ? renderTextWithFootnotesReferences(
                    cleanupEmissionConsumptionMarkup(cae.formattedDischargedCo2Class),
                  )
                : '',
            };
          }

          return null;
        })

        .filter((cae): cae is CaeData => cae !== null);

      if (_caeData.length) setCaeData(_caeData);
    })();
  }, [caeIds]);

  useEffect(() => {
    if (caeData?.length) onCaeReady?.();
  }, [caeData?.length]);

  if (!caeData?.length) return null;

  return (
    <>
      {caeData.map((x, i) => (
        <div key={`cae${i}`}>
          <Text key={`c${i}`} variant={{ type: 'copy', order: '3', style: 'normal' }}>
            {x.consumption}; {x.emissions}
            {x.emissionClass ? '; ' : ''}
            {x.emissionClass ? x.emissionClass : ''}
            {x.dischargedConsumption ? '; ' : ''}
            {x.dischargedConsumption ? x.dischargedConsumption : ''}
            {x.dischargedCO2Class ? `; ` : ''}
            {x.dischargedCO2Class ? x.dischargedCO2Class : ''}
          </Text>
        </div>
      ))}
    </>
  );
};
